/**
 * Jauges : application des effets et bornage.
 *
 * Invariant du moteur : **aucune jauge ne sort jamais de ses bornes**. C'est la
 * seule garantie qui protège l'affichage (une barre à 120 % ou à −30 est
 * incompréhensible pour un élève) et l'équilibrage du jeu.
 */
import type { Content, Effects, Gauge, GaugeId } from './content-schema.js';
import type { GameState } from './state.js';

/** Ramène une valeur entre les bornes d'une jauge. */
export function clampToGauge(jauge: Gauge, valeur: number): number {
  if (Number.isNaN(valeur)) {
    return jauge.valeur_initiale;
  }
  return Math.min(jauge.max, Math.max(jauge.min, valeur));
}

/** Index des jauges par identifiant, pour éviter un `find` par effet appliqué. */
export function gaugesById(content: Content): ReadonlyMap<GaugeId, Gauge> {
  return new Map(content.jauges.map((jauge) => [jauge.id, jauge]));
}

/**
 * Applique un ensemble d'effets aux jauges, borne par borne.
 *
 * Les effets absents laissent la jauge inchangée. Un effet qui viserait une
 * jauge absente du contenu est ignoré — le schéma et les contrôles d'intégrité
 * empêchent déjà ce cas, la garde est une ceinture de sécurité.
 */
export function applyEffects(state: GameState, effects: Effects, content: Content): GameState {
  const index = gaugesById(content);
  const gauges = { ...state.gauges };

  for (const [gaugeId, variation] of Object.entries(effects) as [GaugeId, number | undefined][]) {
    if (variation === undefined) {
      continue;
    }
    const jauge = index.get(gaugeId);
    if (!jauge) {
      continue;
    }
    gauges[gaugeId] = clampToGauge(jauge, gauges[gaugeId] + variation);
  }

  return { ...state, gauges };
}

/** Somme plusieurs ensembles d'effets, jauge par jauge. */
export function sumEffects(...effets: readonly Effects[]): Effects {
  const total: Record<string, number> = {};

  for (const effet of effets) {
    for (const [gaugeId, variation] of Object.entries(effet)) {
      if (variation === undefined) {
        continue;
      }
      total[gaugeId] = (total[gaugeId] ?? 0) + variation;
    }
  }

  return total as Effects;
}

/** Une jauge est-elle dans ses bornes ? Utilisé par les tests de propriété. */
export function isWithinBounds(
  content: Content,
  gauges: Readonly<Record<GaugeId, number>>,
): boolean {
  return content.jauges.every((jauge) => {
    const valeur = gauges[jauge.id];
    return valeur >= jauge.min && valeur <= jauge.max;
  });
}
